import { useMemoizedFn } from 'ahooks';
import { checkVoucherCode, createVoucher, updateVoucher, VoucherBody } from 'services/api/voucher';
import { FormItemProps, message } from 'antd';
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { voucherFormValues2VoAdapter } from './utils';
import { useAuthorization } from 'components/Authorization';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { systemLanguageMap } from '../constants';

const shoeErrorMsg = ({ code, msg }: { code: number; msg: string }, fallback: string) => {
  let errMsg = fallback;
  if (code === 499 && msg) {
    errMsg = msg;
  }
  message.error(errMsg);
};

export const useVoucherFormSubmit = () => {
  const { t } = useTranslate();
  const auth = useAuthorization<Oversea.Token.Payload>();
  const systemLanguage = useSystemLanguageValue();

  const [dynamicRules, setDynamicRules] = useState<Record<string, FormItemProps['rules']>>({});
  const [submitLoading, setSubmitLoading] = useState(false);

  const checkShopeeValues = useMemoizedFn(async (formValues: any, storeId: string) => {
    const { voucherCode } = formValues;
    try {
      const result = await checkVoucherCode({
        internalStoreId: storeId,
        voucherCode,
      });
      if (result) {
        setSubmitLoading(false);
        message.error(t('scene.chatWorkspace.couponForm.error.codeExists'));
        return;
      }
    } catch (err) {
      setSubmitLoading(false);
      message.error(t('scene.chatWorkspace.couponForm.error.codeCheckFailed'));
      return;
    }
  });

  // 编辑提交
  const submitEditFormValues = useMemoizedFn(
    async (
      internalVoucherId: string,
      formValues: any,
      storeId: string,
      channel: General.PlatformUpperCaseTunnel,
    ) => {
      setSubmitLoading(true);
      if (channel === 'SHOPEE') {
        await checkShopeeValues(formValues, storeId);
      }

      const values = voucherFormValues2VoAdapter(formValues, storeId);

      // 编辑优惠券
      try {
        values.internalVoucherId = internalVoucherId;
        values.operatorId = auth?.payload?.id;
        const {
          data: { code, msg },
        } = await updateVoucher({
          ...values,
          language: systemLanguageMap[systemLanguage],
        } as any);
        if (code === 200) {
          message.success(t('scene.chatWorkspace.couponForm.feedback.updateSuccess'));
          return true;
        } else {
          // @ts-ignore
          throw new Error('UpdateVoucherFailed', {
            cause: {
              code,
              msg,
            },
          });
        }
      } catch (err: any) {
        const code = err?.cause?.code || err?.response?.data?.code;
        const msg = err?.cause?.msg || err?.response?.data?.msg;
        shoeErrorMsg(
          {
            code,
            msg,
          },
          t('scene.chatWorkspace.couponForm.feedback.updateFailed'),
        );
        return false;
      } finally {
        setSubmitLoading(false);
      }
    },
  );

  // 创建提交
  const submitCreateFormValues = useMemoizedFn(
    async (formValues: any, storeId: string, channel: General.PlatformUpperCaseTunnel) => {
      setSubmitLoading(true);
      if (channel === 'SHOPEE') {
        await checkShopeeValues(formValues, storeId);
      }

      const values = voucherFormValues2VoAdapter(formValues, storeId);

      // 添加优惠券
      try {
        const {
          data: { code, msg },
        } = await createVoucher({
          ...values,
          operatorId: auth?.payload?.id,
          language: systemLanguageMap[systemLanguage],
        } as VoucherBody);
        if (code === 200) {
          message.success(t('scene.chatWorkspace.couponForm.feedback.createSuccess'));
          return true;
        } else {
          // @ts-ignore
          throw new Error('CreateVoucherFailed', {
            cause: {
              code,
              msg,
            },
          });
        }
      } catch (err: any) {
        const code = err?.cause?.code || err?.response?.data?.code;
        const msg = err?.cause?.msg || err?.response?.data?.msg;
        shoeErrorMsg(
          {
            code,
            msg,
          },
          t('scene.chatWorkspace.couponForm.feedback.createFailed'),
        );

        try {
          if (err?.response?.data?.code === 400) {
            const msgObj = JSON.parse(err?.response?.data?.msg);
            const { filed } = msgObj;
            const filedRule: any = {};
            Object.keys(msgObj).forEach((k) => {
              if (k !== 'filed') {
                filedRule[k] = msgObj[k];
              }
            });
            if (!filedRule.type) {
              filedRule.type = 'number';
            }
            if (filedRule.min && !filedRule.message) {
              filedRule.message = t('common.validation.min', { num: filedRule.min });
            }
            const newRules = { ...dynamicRules };
            if (newRules[filed]) {
              newRules[filed]?.push(filedRule);
            } else {
              newRules[filed] = [filedRule];
            }
            setDynamicRules(newRules);
          }
        } catch (e) {
          // eslint-disable-next-line
          console.error(e);
        }
        return false;
      } finally {
        setSubmitLoading(false);
      }
    },
  );

  return {
    dynamicRules,
    submitLoading,
    submitEditFormValues,
    submitCreateFormValues,
  };
};
